const ALLOWED_TAGS = new Set([
    'P', 'BR', 'DIV', 'SPAN', 'STRONG', 'EM', 'B', 'I', 'U', 'FONT',
    'H2', 'H3', 'H4', 'UL', 'OL', 'LI', 'A', 'BLOCKQUOTE',
    'IMG', 'FIGURE', 'FIGCAPTION', 'VIDEO', 'IFRAME', 'SOURCE',
    'TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TH', 'TD', 'CAPTION',
]);

const STYLE_COLOR_TAGS = new Set([
    'SPAN', 'P', 'DIV', 'H2', 'H3', 'H4', 'STRONG', 'EM', 'B', 'I', 'U', 'LI', 'BLOCKQUOTE', 'FONT',
    'TD', 'TH', 'TR',
]);

const ALLOWED_ATTRS: Record<string, Set<string>> = {
    A: new Set(['href', 'target', 'rel', 'class', 'title']),
    SPAN: new Set(['class', 'style']),
    DIV: new Set(['class', 'style']),
    P: new Set(['class', 'style']),
    H2: new Set(['class', 'style']),
    H3: new Set(['class', 'style']),
    H4: new Set(['class', 'style']),
    STRONG: new Set(['class', 'style']),
    EM: new Set(['class', 'style']),
    B: new Set(['class', 'style']),
    I: new Set(['class', 'style']),
    U: new Set(['class', 'style']),
    LI: new Set(['class', 'style']),
    FONT: new Set(['color', 'face', 'size', 'style']),
    BLOCKQUOTE: new Set(['class', 'style']),
    TABLE: new Set(['class']),
    THEAD: new Set(['class']),
    TBODY: new Set(['class']),
    TFOOT: new Set(['class']),
    TR: new Set(['class', 'style']),
    TH: new Set(['class', 'style', 'colspan', 'rowspan', 'scope']),
    TD: new Set(['class', 'style', 'colspan', 'rowspan']),
    CAPTION: new Set(['class']),
    IMG: new Set(['src', 'alt', 'class', 'loading', 'width', 'height']),
    FIGURE: new Set(['class']),
    VIDEO: new Set(['src', 'controls', 'preload', 'class', 'width', 'height']),
    IFRAME: new Set(['src', 'title', 'allow', 'allowfullscreen', 'loading', 'class', 'frameborder']),
};

const SAFE_MEDIA_HOSTS = /^(https?:\/\/)?([\w.-]+\.)?(youtube\.com|youtube-nocookie\.com|youtu\.be)/i;

const ALLOWED_CLASSES = new Set([
    'btn-download',
    'news-download-buttons',
    'news-video-embed',
    'news-video-file',
    'news-figure',
    'news-text-gold',
    'news-text-muted',
    'news-table',
    'icon',
]);

function sanitizeClassAttr(el: Element): void {
    const raw = el.getAttribute('class');
    if (!raw) return;
    const kept = raw
        .split(/\s+/)
        .filter((c) => ALLOWED_CLASSES.has(c));
    if (kept.length > 0) el.setAttribute('class', kept.join(' '));
    else el.removeAttribute('class');
}

function isSafeLinkUrl(url: string): boolean {
    const trimmed = url.trim();
    if (!trimmed || /^\s*javascript:/i.test(trimmed)) return false;
    if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return true;
    if (trimmed.startsWith('mailto:')) return /^mailto:[^\s@]+@[^\s@]+\.[^\s@]+/i.test(trimmed);
    try {
        const parsed = new URL(trimmed, typeof window !== 'undefined' ? window.location.origin : 'https://local');
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
        return false;
    }
}

function normalizeLinkHref(url: string): string {
    const trimmed = url.trim();
    if (!trimmed) return trimmed;
    if (trimmed.startsWith('/') || /^https?:\/\//i.test(trimmed) || trimmed.startsWith('mailto:')) return trimmed;
    if (/^www\./i.test(trimmed)) return `https://${trimmed}`;
    return trimmed;
}

function isSafeMediaUrl(url: string): boolean {
    const trimmed = url.trim();
    if (!trimmed) return false;
    if (trimmed.startsWith('/uploads/') || trimmed.startsWith('/mount-guide/') || trimmed.startsWith('/assets/')) return true;
    if (trimmed.startsWith('data:image/')) return /^data:image\/(jpeg|png|gif|webp);base64,/i.test(trimmed);
    if (/^\s*javascript:/i.test(trimmed)) return false;
    try {
        const parsed = new URL(trimmed, typeof window !== 'undefined' ? window.location.origin : 'https://local');
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;
        if (parsed.pathname.startsWith('/uploads/')) return true;
        if (SAFE_MEDIA_HOSTS.test(parsed.hostname)) return true;
        const apiOrigin = apiStaticOrigin();
        if (apiOrigin && trimmed.startsWith(apiOrigin) && parsed.pathname.startsWith('/uploads/')) return true;
        return /^https?:\/\//i.test(trimmed);
    } catch {
        return trimmed.startsWith('/');
    }
}

function escapeAttr(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function isSafeCssColor(color: string): boolean {
    const c = color.trim();
    if (!c) return false;
    if (/^#[0-9a-f]{3,8}$/i.test(c)) return true;
    if (/^rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)$/i.test(c)) return true;
    if (/^rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*(0|1|0?\.\d+)\s*\)$/i.test(c)) return true;
    const named = new Set([
        'white', 'black', 'gold', 'silver', 'gray', 'grey', 'red', 'green', 'blue',
        'yellow', 'orange', 'cyan', 'magenta', 'purple', 'pink', 'brown',
    ]);
    return named.has(c.toLowerCase());
}

function sanitizeTextStyle(style: string): string | null {
    const parts: string[] = [];

    const colorMatch = style.match(/\bcolor\s*:\s*([^;]+)/i);
    if (colorMatch) {
        const color = colorMatch[1].trim();
        if (isSafeCssColor(color)) parts.push(`color: ${color}`);
    }

    const familyMatch = style.match(/\bfont-family\s*:\s*([^;]+)/i);
    if (familyMatch) {
        const family = sanitizeFontFamilyValue(familyMatch[1].trim());
        if (family) parts.push(`font-family: ${family}`);
    }

    const sizeMatch = style.match(/\bfont-size\s*:\s*([^;]+)/i);
    if (sizeMatch) {
        const size = sanitizeFontSizeValue(sizeMatch[1].trim());
        if (size) parts.push(`font-size: ${size}`);
    }

    return parts.length > 0 ? parts.join('; ') : null;
}

const ALLOWED_FONT_KEYS = new Set([
    'arial', 'helvetica', 'georgia', 'times new roman', 'courier new',
    'roboto', 'cinzel', 'marcellus', 'verdana', 'tahoma', 'impact',
]);

const FONT_FAMILY_MAP: Record<string, string> = {
    arial: 'Arial, Helvetica, sans-serif',
    helvetica: 'Helvetica, Arial, sans-serif',
    georgia: 'Georgia, "Times New Roman", serif',
    'times new roman': '"Times New Roman", Times, serif',
    'courier new': '"Courier New", Courier, monospace',
    roboto: 'Roboto, sans-serif',
    cinzel: 'Cinzel, serif',
    marcellus: 'Marcellus, serif',
    verdana: 'Verdana, sans-serif',
    tahoma: 'Tahoma, sans-serif',
    impact: 'Impact, Haettenschweiler, sans-serif',
};

function sanitizeFontFamilyValue(raw: string): string | null {
    const first = raw
        .split(',')[0]
        .trim()
        .replace(/^['"]|['"]$/g, '')
        .toLowerCase();
    if (!ALLOWED_FONT_KEYS.has(first)) return null;
    return FONT_FAMILY_MAP[first] ?? null;
}

function sanitizeFontSizeValue(raw: string): string | null {
    const v = raw.trim().toLowerCase();
    if (/^\d{1,2}px$/.test(v)) {
        const n = parseInt(v, 10);
        if (n >= 10 && n <= 72) return `${n}px`;
    }
    if (/^\d{1,2}pt$/.test(v)) {
        const n = parseInt(v, 10);
        if (n >= 8 && n <= 54) return `${n}pt`;
    }
    if (/^(\d(\.\d)?|\d{1,2}(\.\d)?)(em|rem)$/.test(v)) {
        const n = parseFloat(v);
        if (n >= 0.5 && n <= 4) return v;
    }
    return null;
}

function fontSizeFromHtmlSize(size: string): string | null {
    const map: Record<string, string> = {
        '1': '10px',
        '2': '13px',
        '3': '16px',
        '4': '18px',
        '5': '24px',
        '6': '32px',
        '7': '48px',
    };
    return map[size.trim()] ?? null;
}

function sanitizeColorAttr(color: string): string | null {
    const c = color.trim();
    if (!c || /^\s*javascript:/i.test(c)) return null;
    if (isSafeCssColor(c)) return c;
    return null;
}

/** Converte BBCode comum (img, color) para HTML antes da sanitização. */
export function convertBbCodeToHtml(content: string): string {
    let result = content;

    result = result.replace(/\[img\]\s*([\s\S]*?)\s*\[\/img\]/gi, (_, url: string) => {
        const src = resolvePublicMediaUrl(url.trim()) ?? url.trim();
        if (!src || !isSafeMediaUrl(src)) {
            return escapeHtml(`[img]${url}[/img]`);
        }
        return `<figure class="news-figure"><img src="${escapeAttr(src)}" alt="" loading="lazy" /></figure>`;
    });

    result = result.replace(/\[color=([^\]]+)\]([\s\S]*?)\[\/color\]/gi, (_, color: string, text: string) => {
        const safe = sanitizeColorAttr(color);
        if (!safe) return text;
        return `<span style="color: ${safe}">${text}</span>`;
    });

    result = result.replace(/\[size=([^\]]+)\]([\s\S]*?)\[\/size\]/gi, (_, size: string, text: string) => {
        const safe = sanitizeFontSizeValue(size);
        if (!safe) return text;
        return `<span style="font-size: ${safe}">${text}</span>`;
    });

    result = result.replace(/\[font=([^\]]+)\]([\s\S]*?)\[\/font\]/gi, (_, font: string, text: string) => {
        const safe = sanitizeFontFamilyValue(font);
        if (!safe) return text;
        return `<span style="font-family: ${safe}">${text}</span>`;
    });

    result = result.replace(/\[url=([^\]]+)\]([\s\S]*?)\[\/url\]/gi, (_, href: string, text: string) => {
        const safeHref = normalizeLinkHref(href);
        if (!isSafeLinkUrl(safeHref)) return text;
        const label = (text || safeHref).trim() || safeHref;
        return `<a href="${escapeAttr(safeHref)}" target="_blank" rel="noopener noreferrer">${label}</a>`;
    });

    result = result.replace(/\[url\]([\s\S]*?)\[\/url\]/gi, (_, href: string) => {
        const safeHref = normalizeLinkHref(href.trim());
        if (!isSafeLinkUrl(safeHref)) return href;
        return `<a href="${escapeAttr(safeHref)}" target="_blank" rel="noopener noreferrer">${safeHref}</a>`;
    });

    result = result.replace(/\[b\]([\s\S]*?)\[\/b\]/gi, '<strong>$1</strong>');
    result = result.replace(/\[i\]([\s\S]*?)\[\/i\]/gi, '<em>$1</em>');
    result = result.replace(/\[u\]([\s\S]*?)\[\/u\]/gi, '<u>$1</u>');

    return result;
}

function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function looksLikeHtml(content: string): boolean {
    return /<[a-z][\s\S]*>/i.test(content);
}

function linkifyPlainSegment(text: string): string {
    const urlPattern = /(\bhttps?:\/\/[^\s<]+|\bwww\.[^\s<]+)/gi;
    let result = '';
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = urlPattern.exec(text)) !== null) {
        result += escapeHtml(text.slice(lastIndex, match.index));
        const raw = match[0];
        const href = normalizeLinkHref(raw);
        if (isSafeLinkUrl(href)) {
            result += `<a href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(raw)}</a>`;
        } else {
            result += escapeHtml(raw);
        }
        lastIndex = match.index + raw.length;
    }

    result += escapeHtml(text.slice(lastIndex));
    return result;
}

function plainTextToHtml(content: string): string {
    return content
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .map((line) => `<p>${linkifyPlainSegment(line)}</p>`)
        .join('');
}

/** Transforma em link as URLs cruas dentro de nós de texto do HTML (sem tocar nas já linkadas). */
function linkifyTextNodes(container: Element): void {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const pending: { node: Text; html: string }[] = [];
    let currentNode: Text | null;

    const urlHint = /(https?:\/\/|www\.)/i;
    while ((currentNode = walker.nextNode() as Text | null) !== null) {
        const node = currentNode;
        if (!node.nodeValue || !urlHint.test(node.nodeValue)) continue;

        let parent: Node | null = node.parentNode;
        let insideAnchor = false;
        while (parent && parent !== container) {
            if (parent instanceof Element && parent.tagName === 'A') {
                insideAnchor = true;
                break;
            }
            parent = parent.parentNode;
        }
        if (insideAnchor) continue;

        const html = linkifyPlainSegment(node.nodeValue);
        if (html !== escapeHtml(node.nodeValue)) {
            pending.push({ node, html });
        }
    }

    for (const { node, html } of pending) {
        const holder = document.createElement('span');
        holder.innerHTML = html;
        const fragment = document.createDocumentFragment();
        while (holder.firstChild) {
            fragment.appendChild(holder.firstChild);
        }
        node.replaceWith(fragment);
    }
}

function sanitizeElement(el: Element): void {
    const tag = el.tagName.toUpperCase();
    if (!ALLOWED_TAGS.has(tag)) {
        el.replaceWith(...Array.from(el.childNodes));
        return;
    }

    const allowed = ALLOWED_ATTRS[tag];
    Array.from(el.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (!allowed?.has(name)) {
            el.removeAttribute(attr.name);
            return;
        }
        if (name === 'href') {
            const normalized = normalizeLinkHref(attr.value);
            if (!isSafeLinkUrl(normalized)) {
                el.removeAttribute(attr.name);
            } else if (normalized !== attr.value) {
                el.setAttribute('href', normalized);
            }
            return;
        }
        if (name === 'src' && /^\s*javascript:/i.test(attr.value)) {
            el.removeAttribute(attr.name);
            return;
        }
        if (name === 'src' && !isSafeMediaUrl(attr.value)) {
            el.removeAttribute(attr.name);
            return;
        }
        if (name === 'src') {
            const resolved = resolvePublicMediaUrl(attr.value);
            if (resolved) el.setAttribute('src', resolved);
            return;
        }
        if (name === 'class') {
            sanitizeClassAttr(el);
            return;
        }
        if (name === 'style' && STYLE_COLOR_TAGS.has(tag)) {
            const safeStyle = sanitizeTextStyle(attr.value);
            if (safeStyle) el.setAttribute('style', safeStyle);
            else el.removeAttribute('style');
            return;
        }
        if (name === 'color' && tag === 'FONT') {
            const safe = sanitizeColorAttr(attr.value);
            if (safe) el.setAttribute('color', safe);
            else el.removeAttribute('color');
            return;
        }
        if (name === 'face' && tag === 'FONT') {
            const safe = sanitizeFontFamilyValue(attr.value);
            if (safe) el.setAttribute('face', attr.value.trim());
            else el.removeAttribute('face');
            return;
        }
        if (name === 'size' && tag === 'FONT') {
            const safe = fontSizeFromHtmlSize(attr.value);
            if (safe) el.setAttribute('size', attr.value.trim());
            else el.removeAttribute('size');
            return;
        }
        if ((name === 'colspan' || name === 'rowspan') && (tag === 'TD' || tag === 'TH')) {
            const n = parseInt(attr.value, 10);
            if (n >= 1 && n <= 50) el.setAttribute(attr.name, String(n));
            else el.removeAttribute(attr.name);
            return;
        }
    });

    if (tag === 'FONT') {
        const parts: string[] = [];
        const color = el.getAttribute('color');
        const face = el.getAttribute('face');
        const size = el.getAttribute('size');
        if (color) {
            const safe = sanitizeColorAttr(color);
            if (safe) parts.push(`color: ${safe}`);
        }
        if (face) {
            const safe = sanitizeFontFamilyValue(face);
            if (safe) parts.push(`font-family: ${safe}`);
        }
        if (size) {
            const safe = fontSizeFromHtmlSize(size);
            if (safe) parts.push(`font-size: ${safe}`);
        }
        const existing = el.getAttribute('style');
        if (existing) {
            const safe = sanitizeTextStyle(existing);
            if (safe) parts.push(safe);
        }
        if (parts.length > 0) el.setAttribute('style', parts.join('; '));
    }

    if (tag === 'IFRAME') {
        const src = el.getAttribute('src') ?? '';
        if (!SAFE_MEDIA_HOSTS.test(src)) {
            el.remove();
            return;
        }
    }

    if (tag === 'A') {
        const href = el.getAttribute('href');
        if (href) {
            el.setAttribute('rel', 'noopener noreferrer');
            if (!el.getAttribute('target')) {
                el.setAttribute('target', '_blank');
            }
        }
    }

    Array.from(el.children).forEach((child) => sanitizeElement(child));
}

/** Sanitiza HTML do admin e devolve string segura para dangerouslySetInnerHTML. */
export function prepareNewsContentHtml(content: string): string {
    const raw = convertBbCodeToHtml(content?.trim() ?? '');
    if (!raw) return '';

    if (!looksLikeHtml(raw)) {
        return plainTextToHtml(raw);
    }

    const doc = new DOMParser().parseFromString(raw, 'text/html');
    Array.from(doc.body.childNodes).forEach((node) => {
        if (node instanceof Element) {
            sanitizeElement(node);
        }
    });

    Array.from(doc.body.querySelectorAll('font')).forEach((font) => {
        const span = doc.createElement('span');
        const parts: string[] = [];
        const color = font.getAttribute('color');
        const face = font.getAttribute('face');
        const size = font.getAttribute('size');
        const existing = font.getAttribute('style');

        if (color) {
            const safe = sanitizeColorAttr(color);
            if (safe) parts.push(`color: ${safe}`);
        }
        if (face) {
            const safe = sanitizeFontFamilyValue(face);
            if (safe) parts.push(`font-family: ${safe}`);
        }
        if (size) {
            const safe = fontSizeFromHtmlSize(size);
            if (safe) parts.push(`font-size: ${safe}`);
        }
        if (existing) {
            const safe = sanitizeTextStyle(existing);
            if (safe) parts.push(safe);
        }
        if (parts.length > 0) span.setAttribute('style', parts.join('; '));
        span.innerHTML = font.innerHTML;
        font.replaceWith(span);
    });

    linkifyTextNodes(doc.body);

    return doc.body.innerHTML;
}

/** Texto plano para listagens (accordion, cards). */
export function newsContentPreview(content: string, maxLength = 220): string {
    const html = prepareNewsContentHtml(content);
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const text = (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength).trim()}…`;
}

function apiStaticOrigin(): string | undefined {
    const base = process.env.REACT_APP_API_URL?.trim();
    if (base) {
        try {
            const u = new URL(base.endsWith('/') ? base : `${base}/`);
            u.pathname = '/';
            return u.origin;
        } catch {
            /* fall through */
        }
    }
    if (typeof window !== 'undefined' && window.location?.origin) {
        return window.location.origin;
    }
    return undefined;
}

function isApiMediaPath(path: string): boolean {
    return path.startsWith('/assets/') || path.startsWith('/uploads/') || path.startsWith('/mount-guide/');
}

/** Corrige URLs de mídia (uploads locais, localhost da API, links externos). */
export function resolvePublicMediaUrl(url?: string | null): string | undefined {
    const raw = url?.trim();
    if (!raw) return undefined;

    if (/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\//i.test(raw)) {
        try {
            const pathOnly = new URL(raw).pathname;
            if (isApiMediaPath(pathOnly)) {
                const origin = apiStaticOrigin();
                return origin ? `${origin}${pathOnly}` : pathOnly;
            }
            return pathOnly;
        } catch {
            return undefined;
        }
    }

    if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('data:')) {
        return raw;
    }

    if (raw.startsWith('/')) {
        if (isApiMediaPath(raw)) {
            const origin = apiStaticOrigin();
            return origin ? `${origin}${raw}` : raw;
        }
        return raw;
    }

    const path = `/${raw}`;
    if (isApiMediaPath(path)) {
        const origin = apiStaticOrigin();
        return origin ? `${origin}${path}` : path;
    }
    return path;
}
